import { useEffect, useRef, useState } from 'react';

// Material UI
import {
    styled,
    Paper,
    IconButton,
    Box,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

// Utility
import { getRelativeTime } from 'utility/dates';
import { normalizeCharacters } from 'utility/strings';
import moment from 'moment-timezone';

export default function ErrorLogList(props) {
    let errorLog = props.errorLog;

    // Search and Filter
    const [searchValue, setSearchValue] = useState('');
    const [selectedError, setSelectedError] = useState(null);
    const [copiedField, setCopiedField] = useState(null);
    const copiedTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            window.clearTimeout(copiedTimerRef.current);
        };
    }, []);

    // ***** Filter by Search Query ***** //

    if (searchValue.trim()) {
        const searchStr = normalizeCharacters(searchValue.trim()); // Normalize string to remove accents and diacritics

        const searchRegex = new RegExp(searchStr.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');

        errorLog = errorLog.filter((log) => {
            const host = normalizeCharacters(log.host);
            const event = normalizeCharacters(log.event);
            const deviceId = normalizeCharacters(log.deviceId);
            const deviceName = normalizeCharacters(log.deviceName);
            const code = normalizeCharacters(log.code);
            const message = normalizeCharacters(log.message);
            return (
                searchRegex.test(event) ||
                searchRegex.test(host) ||
                searchRegex.test(deviceId) ||
                searchRegex.test(deviceName) ||
                searchRegex.test(code) ||
                searchRegex.test(message)
            );
        });
    }

    const rows = errorLog.map((e) => ({
        id: e.id,
        c1: { event: e.event, host: e.host },
        c2: {
            deviceName: e.deviceName,
            deviceId: e.deviceId,
            deviceBrand: e.deviceBrand,
            deviceModel: e.deviceModel,
            deviceOsName: e.deviceOsName,
            deviceOsVersion: e.deviceOsVersion,
            deviceCarrier: e.deviceCarrier,
            deviceCellularGeneration: e.deviceCellularGeneration,
            deviceNetworkType: e.deviceNetworkType,
        },
        c3: e.appVersion,
        c4: { code: e.code || '-', message: e.message || e.data || '-', data: e.data || '-' },
        c5: {
            fullDate: moment(e.date).format('MMM D, h:mma'),
            shortDate: getRelativeTime(e.date, 'short'),
        },
    }));

    const handleRowClick = (params) => {
        setSelectedError({
            ...params.row.c4,
            event: params.row.c1,
            device: params.row.c2,
            ver: params.row.c3,
            when: params.row.c5.fullDate,
        });
        setCopiedField(null);
    };

    const handleDialogClose = () => {
        setSelectedError(null);
        setCopiedField(null);
        window.clearTimeout(copiedTimerRef.current);
    };

    const copyToClipboard = async (field, value) => {
        if (!value) {
            return;
        }

        await navigator.clipboard.writeText(value);
        setCopiedField(field);

        window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = window.setTimeout(() => {
            setCopiedField(null);
        }, 1500);
    };

    // Note: When changing column order, update field references (ie: c1, c2) in callbacks like handleRowClick
    const columns = [
        {
            field: 'c1',
            headerName: 'Event',
            minWidth: 160,
            flex: 1,
            hideSortIcons: true,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" display="block">
                        {params.value.host}
                    </Typography>
                    <Typography variant="caption" display="block">
                        {params.value.event}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'c2',
            headerName: 'Device',
            width: 140,
            hideSortIcons: true,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2">{params.value.deviceName}</Typography>
                    <Typography variant="caption">{params.value.deviceId}</Typography>
                </Box>
            ),
        },
        { field: 'c3', headerName: 'Ver', width: 80, hideSortIcons: true },
        {
            field: 'c4',
            headerName: 'Error',
            flex: 2,
            minWidth: 200,
            hideSortIcons: true,
            renderCell: (params) => (
                <Typography variant="body2" display="block">
                    {params.value.message}
                </Typography>
            ),
        },
        {
            field: 'c5',
            headerName: 'When',
            width: 80,
            hideSortIcons: true,
            renderCell: (params) => (
                <Typography variant="body2" display="block">
                    {params.value.shortDate}
                </Typography>
            ),
        },
    ];

    return (
        <Container>
            <Box flexGrow={1} width={'100%'}>
                <Dialog open={selectedError !== null} onClose={handleDialogClose} maxWidth="sm" fullWidth>
                    <DialogTitle>Error Details</DialogTitle>
                    <DialogContent dividers>
                        <Box mb={2}>
                            <Box mb={1}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                    <Typography variant="overline" color="text.secondary">
                                        Event
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedError?.event?.host}
                                    {' '}
                                    {selectedError?.event?.event}
                                </Typography>
                            </Box>

                            <Box
                                display="grid"
                                gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                                columnGap={2}
                                rowGap={1}
                                mb={1}
                            >
                                <DeviceDetail label="App Version" value={selectedError?.ver} />
                                <DeviceDetail label="When" value={selectedError?.when} />
                                <DeviceDetail label="Device Name" value={selectedError?.device?.deviceName} />
                                <DeviceDetail label="Device ID" value={selectedError?.device?.deviceId} />
                                <DeviceDetail label="Brand" value={selectedError?.device?.deviceBrand} />
                                <DeviceDetail label="Model" value={selectedError?.device?.deviceModel} />
                                <DeviceDetail label="OS" value={selectedError?.device?.deviceOsName} />
                                <DeviceDetail label="OS version" value={selectedError?.device?.deviceOsVersion} />
                                <DeviceDetail label="Carrier" value={selectedError?.device?.deviceCarrier} />
                                <DeviceDetail
                                    label="Cellular generation"
                                    value={selectedError?.device?.deviceCellularGeneration}
                                />
                                <DeviceDetail
                                    label="Network type"
                                    value={selectedError?.device?.deviceNetworkType}
                                />
                            </Box>

                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <Typography variant="overline" color="text.secondary">
                                    Code
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {copiedField === 'code' && (
                                        <Typography variant="caption" color="text.secondary">
                                            Text Copied!
                                        </Typography>
                                    )}
                                    <IconButton
                                        size="small"
                                        aria-label="Copy error code"
                                        onClick={() => copyToClipboard('code', selectedError?.code)}
                                    >
                                        <ContentCopyOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {selectedError?.code}
                            </Typography>
                        </Box>

                        <Box mb={2}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <Typography variant="overline" color="text.secondary">
                                    Message
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {copiedField === 'message' && (
                                        <Typography variant="caption" color="text.secondary">
                                            Text Copied!
                                        </Typography>
                                    )}
                                    <IconButton
                                        size="small"
                                        aria-label="Copy error message"
                                        onClick={() => copyToClipboard('message', selectedError?.message)}
                                    >
                                        <ContentCopyOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', minWidth: '100%' }}>
                                    {selectedError?.message}
                                </Typography>
                            </Box>
                        </Box>

                        <Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <Typography variant="overline" color="text.secondary">
                                    Error Data
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {copiedField === 'data' && (
                                        <Typography variant="caption" color="text.secondary">
                                            Text Copied!
                                        </Typography>
                                    )}
                                    <IconButton
                                        size="small"
                                        aria-label="Copy error data"
                                        onClick={() => copyToClipboard('data', selectedError?.data)}
                                    >
                                        <ContentCopyOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', minWidth: '100%' }}>
                                    {selectedError?.data}
                                </Typography>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}>Close</Button>
                    </DialogActions>
                </Dialog>

                <TableContainer>
                    <DG
                        components={{ Toolbar: Toolbar }}
                        componentsProps={{
                            toolbar: {
                                value: searchValue,
                                onChange: (event) => setSearchValue(event.target.value),
                                clearSearch: () => setSearchValue(''),
                            },
                        }}
                        rows={rows}
                        columns={columns}
                        rowHeight={70}
                        disableColumnMenu
                        disableSelectionOnClick
                        onRowClick={handleRowClick}
                        sortingOrder={['desc', 'asc']}
                    />
                </TableContainer>
            </Box>
        </Container>
    );
}

function DeviceDetail({ label, value }) {
    return (
        <Box>
            <Typography variant="overline" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

function Toolbar(props) {
    const { value, onChange, clearSearch } = props;

    return (
        <SearchBar
            autoFocus
            variant="outlined"
            size="small"
            value={value}
            onChange={onChange}
            placeholder="Search..."
            InputProps={{
                startAdornment: <SearchIcon fontSize="small" />,
                endAdornment: (
                    <IconButton
                        title="Clear"
                        aria-label="Clear"
                        size="small"
                        style={{ visibility: value ? 'visible' : 'hidden' }}
                        onClick={clearSearch}
                    >
                        <ClearIcon fontSize="small" />
                    </IconButton>
                ),
            }}
        />
    );
}

const DG = styled(DataGrid)(({ theme }) => ({
    '& .MuiDataGrid-footerContainer': {
        justifyContent: 'flex-start',
    },
    '& .MuiDataGrid-row:hover': {
        cursor: 'pointer',
    },
}));

const Container = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
}));

const TableContainer = styled(Box)(({ theme }) => ({
    height: '100%',
    width: '100%',
}));

const SearchBar = styled(TextField)(({ theme }) => ({
    margin: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '360px',
}));
